import { useCallback, useMemo } from "react";

import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { teamMembershipListQueryDocument } from "./team-membership.gql";

export interface UseGetTeamMembershipListProps {
  teamId: string;
  enabled?: boolean;
}

export const useTeamMembershipList = ({ teamId, enabled = true }: UseGetTeamMembershipListProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.teams.membershipList(teamId),
    queryFn: async () => {
      const client = createOperationClient("teamMembershipList");
      return await client.request(teamMembershipListQueryDocument, { input: teamId });
    },
    enabled: !!teamId && enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes for list data
    refetchOnMount: true, // Always refetch when component mounts
  });

  const teamMembershipList = useMemo(() => {
    if (!queryResult.data) {
      return [];
    }
    return queryResult.data.teamMembershipList;
  }, [queryResult.data]);

  const activeTeamMembershipList = useMemo(() => {
    if (!queryResult.data) {
      return [];
    }
    return queryResult.data.teamMembershipList.slice().filter((teamMembership) => teamMembership.status === "ACTIVE");
  }, [queryResult.data]);

  const getTeamMembershipForUser = useCallback(
    (userId: string) => {
      return teamMembershipList.find((teamMembership) => teamMembership.userId === userId);
    },
    [teamMembershipList],
  );

  const getTeamMembershipById = useCallback(
    (id: string) => {
      return teamMembershipList.find((teamMembership) => teamMembership.id === id);
    },
    [teamMembershipList],
  );

  return {
    queryResult,
    activeTeamMembershipList,
    teamMembershipList,
    getTeamMembershipForUser,
    getTeamMembershipById,
    loading: queryResult.isLoading,
    error: queryResult.error,
  };
};
